import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import type { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { useGlobalStore } from '../stores/globalStore';
import { useConnectionStore } from '../stores/connectionStore';
import { resolvePageTitle } from './pageTitles';

/**
 * Browser telemetry to the same Application Insights resource as the API
 * ("Dietech"): page views, unhandled errors and page load timings.
 *
 * - Off unless VITE_APPINSIGHTS_CONNECTION_STRING is set (only .env.production
 *   sets it), and off on localhost, so dev servers, Playwright and `vite
 *   preview` never write test traffic into production the way the API test
 *   suite does.
 * - API calls are NOT tracked (ajax and fetch tracking are off). The API
 *   already records every request, and tracking them here would add
 *   correlation headers to cross-origin calls.
 * - Page views are named by route ("Resident Details"), never by facility or
 *   resident, and carry the path only: the query string can hold a facility
 *   window handoff (see utils/facilityWindow.ts).
 * - The signed-in login is sent as the authenticated user id, lower-cased like
 *   the API's RequestIdentityTelemetryInitializer, so browser and API rows for
 *   one person match.
 *
 * The SDK is loaded with a dynamic import after the first render, so it never
 * sits on the startup path. Page views made before it loads are queued.
 */

let ai: ApplicationInsights | null = null;
let starting = false;
let queued: Array<{ name: string; path: string }> = [];
let queuedEvents: Array<{ name: string; properties?: Record<string, string> }> = [];
let lastTrackedPath: string | null = null;

/** Most custom events kept while the SDK loads; a page never needs more than a few. */
const MAX_QUEUED_EVENTS = 50;

/** Whether this page should send telemetry at all. */
export function telemetryEnabled(
  connectionString: string | undefined = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING,
  hostname: string = window.location.hostname,
): boolean {
  if (!connectionString) return false;
  return !(hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]');
}

/** Facility, connection, build and login stamped on every browser item. */
export function stampItem(item: ITelemetryItem): void {
  const { facilityId, userEmail } = useGlobalStore.getState();
  const connectionId = useConnectionStore.getState().selectedConnection?.connectionId;
  item.data = {
    ...item.data,
    FacilityId: facilityId || undefined,
    ConnectionId: connectionId || undefined,
    AppVersion: import.meta.env.VITE_APP_VERSION || 'dev',
  };
  const login = userEmail?.trim().toLowerCase();
  if (login) {
    item.tags = { ...item.tags, 'ai.user.authUserId': login };
  }
}

/** Load the SDK. Call once at startup; a no-op where telemetry is off. */
export async function startTelemetry(): Promise<void> {
  if (ai || starting || !telemetryEnabled()) return;
  starting = true;
  try {
    const { ApplicationInsights } = await import('@microsoft/applicationinsights-web');
    const instance = new ApplicationInsights({
      config: {
        connectionString: import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING,
        disableAjaxTracking: true,
        disableFetchTracking: true,
        enableAutoRouteTracking: false,
        autoTrackPageVisitTime: false,
      },
    });
    instance.loadAppInsights();
    instance.addTelemetryInitializer((item) => { stampItem(item); });
    ai = instance;
    const pending = queued;
    queued = [];
    pending.forEach((p) => send(p.name, p.path));
    const events = queuedEvents;
    queuedEvents = [];
    events.forEach((e) => instance.trackEvent({ name: e.name }, e.properties));
  } catch {
    // Telemetry must never break the site: an ad blocker or a failed chunk
    // load just means no browser telemetry for this tab.
  } finally {
    starting = false;
  }
}

function send(name: string, path: string): void {
  ai?.trackPageView({ name, uri: window.location.origin + path });
}

/** Record a page view for `pathname`; repeats of the same path are ignored. */
export function trackPageView(pathname: string): void {
  if (pathname === lastTrackedPath) return;
  lastTrackedPath = pathname;
  const name = resolvePageTitle(pathname);
  if (ai) send(name, pathname);
  else if (telemetryEnabled()) queued.push({ name, path: pathname });
}

/**
 * Record a custom event ("ReportViewerAction" with `{ action: 'print' }`).
 * Properties must not carry resident names or other PHI: action names and
 * ids only. A no-op where telemetry is off.
 */
export function trackEvent(name: string, properties?: Record<string, string>): void {
  try {
    if (ai) ai.trackEvent({ name }, properties);
    else if (telemetryEnabled() && queuedEvents.length < MAX_QUEUED_EVENTS) queuedEvents.push({ name, properties });
  } catch {
    // Telemetry must never break the action being recorded.
  }
}

/** Mount once inside the router. Sends a page view for each pathname. */
export function PageViewTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);
  return null;
}

/** Test seam: forget the SDK instance, the queue and the last path. */
export function resetTelemetry(): void {
  ai = null;
  starting = false;
  queued = [];
  queuedEvents = [];
  lastTrackedPath = null;
}

/** Test seam: custom events waiting for the SDK. */
export function queuedCustomEvents(): ReadonlyArray<{ name: string; properties?: Record<string, string> }> {
  return queuedEvents;
}

/** Test seam: page views waiting for the SDK. */
export function queuedPageViews(): ReadonlyArray<{ name: string; path: string }> {
  return queued;
}
